// "Ship in own packaging" on the pack screen: which units go into the new
// container, and which box each one comes out of. Pure, so the dialog can
// show the plan before Confirm and the pack screen can apply the same plan.
import en from "../../i18n/en";
import pl from "../../i18n/pl";
import type { DispatchBox, OwnPackagingSpec } from "../../types/Dispatch";

/** A weighed own-packaging container's contents weight, or null when it was
 *  never weighed or its contents changed since (the reading described the
 *  parcel as it was, so it stops counting the moment a unit leaves or joins). */
export const measuredContentsGrams = (b: DispatchBox | undefined | null): number | null => {
    if (!b) return null;
    const grams = Number(b.measuredWeightGrams);
    const units = Number(b.measuredUnits);
    return grams > 0 && units > 0 && b.items.length === units ? grams : null;
};

/** What the own-packaging dialog opens with.
 *  `source`: where the dims came from — a saved measurement of this SKU
 *  ("sku"), of another SKU with the same ASIN ("asin"), the product's
 *  catalogue size ("catalogue"), or nothing (null, blank fields).
 *  `unitWeightG`: one unit's weight, so the field can scale with Units. */
export type OwnPackagingDefaults = {
    length: string;
    width: string;
    height: string;
    unitWeightG: number | null;
    source: "sku" | "asin" | "catalogue" | null;
    spec: OwnPackagingSpec | null;
};

type DefaultableItem = {
    length_cm?: number | string | null;
    width_cm?: number | string | null;
    height_cm?: number | string | null;
    weight_grams?: number | null;
};

const positive = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/** Prefill order: the saved measurement (newest of SKU / ASIN), then the
 *  product's own size and weight, then blank. A saved measurement without a
 *  weight still takes the catalogue weight. */
export const ownPackagingDefaults = (item: DefaultableItem, spec?: OwnPackagingSpec | null): OwnPackagingDefaults => {
    const catalogueWeight = positive(item.weight_grams);
    if (spec && positive(spec.length_cm) && positive(spec.width_cm) && positive(spec.height_cm)) {
        return {
            length: String(spec.length_cm),
            width: String(spec.width_cm),
            height: String(spec.height_cm),
            unitWeightG: positive(spec.weight_grams) ?? catalogueWeight,
            source: spec.matched_by === "asin" ? "asin" : "sku",
            spec,
        };
    }
    const l = positive(item.length_cm), w = positive(item.width_cm), h = positive(item.height_cm);
    if (l && w && h) {
        return { length: String(l), width: String(w), height: String(h), unitWeightG: catalogueWeight, source: "catalogue", spec: null };
    }
    return { length: "", width: "", height: "", unitWeightG: catalogueWeight, source: null, spec: null };
};

/** Grams to the dialog's kg field ("" for unknown), without float noise. */
export const kgField = (grams: number | null | undefined): string =>
    grams != null && grams > 0 ? String(Math.round(grams) / 1000) : "";

// A box tab counts as assigned once the operator has scanned a real box
// (boxRef) OR entered custom dimensions via "+ › Other size". Drives both
// the Complete gate and the "needs a scan" tab styling.
export const isBoxAssigned = (b: DispatchBox) =>
    !!b.boxRef || (!!b.boxLengthMm && !!b.boxWidthMm && !!b.boxHeightMm);

// The desk session saves the box name as it was shown, so an own-packaging
// box made in either language is recognised.
const OWN_PACKAGING_NAMES = new Set([en.ship_in_packaging_box_name, pl.ship_in_packaging_box_name]);

/** A container made by "Ship in own packaging" (no catalogue box behind it). */
export const isOwnPackagingBox = (b: DispatchBox) =>
    !b.boxRef && !!b.boxName && OWN_PACKAGING_NAMES.has(b.boxName);

type PlannableItem = { sku: string; quantity: number; assignedBoxes: number[] };

/** Units taken out of one box. `boxed` = a real (scanned or measured) box
 *  the packer must physically empty; false = a tab still waiting for its
 *  box, where the unit is merely scanned. `left` = units still in it after. */
export type OwnPackagingSource = { boxId: number; units: number; boxed: boolean; left: number };

export type OwnPackagingPlan = {
    /** Units in the new container. */
    total: number;
    /** Of those, units not scanned yet — they count as scanned. */
    fresh: number;
    /** Indexes into item.assignedBoxes of the units that move. */
    takenIndexes: number[];
    fromBoxes: OwnPackagingSource[];
};

/** Box entries of the line's scanned units that may move, in the order they
 *  move: units in a tab still waiting for its box first, then units in a
 *  real box, most recently packed first. Units already in own packaging
 *  stay put. */
const movableEntries = (item: PlannableItem, boxes: DispatchBox[]) => {
    const entries = item.assignedBoxes.map((boxId, index) => ({ boxId, index, box: boxes.find(b => b.id === boxId) }));
    const waiting = entries.filter(e => !e.box || !isBoxAssigned(e.box));
    const boxed = entries.filter(e => !!e.box && isBoxAssigned(e.box) && !isOwnPackagingBox(e.box)).reverse();
    return [...waiting, ...boxed];
};

/** How many of the line's units could still go into own packaging. */
export const ownPackagingUnits = (item: PlannableItem, boxes: DispatchBox[], unscanned: number) =>
    Math.max(0, unscanned) + movableEntries(item, boxes).length;

/**
 * Where `units` for one own-packaging container come from.
 *
 *   prefer "boxed"  the line's package button: scanned units leave their
 *                   boxes first, then unscanned units are used.
 *   prefer "fresh"  "+ › Own packaging" then an item scan: the unit in hand
 *                   is an unscanned one while the line still has any.
 *
 * `units` is clamped to what the line can give.
 */
export const planOwnPackaging = (
    item: PlannableItem,
    boxes: DispatchBox[],
    { units, unscanned, prefer }: { units: number; unscanned: number; prefer: "boxed" | "fresh" },
): OwnPackagingPlan => {
    const movable = movableEntries(item, boxes);
    const freeUnits = Math.max(0, unscanned);
    const available = freeUnits + movable.length;
    const total = Math.max(0, Math.min(Math.floor(units) || 1, available));
    const fresh = prefer === "fresh"
        ? Math.min(total, freeUnits)
        : Math.max(0, total - movable.length);
    const taken = movable.slice(0, total - fresh);

    const fromBoxes: OwnPackagingSource[] = [];
    for (const entry of taken) {
        const existing = fromBoxes.find(s => s.boxId === entry.boxId);
        if (existing) { existing.units += 1; continue; }
        fromBoxes.push({
            boxId: entry.boxId,
            units: 1,
            boxed: !!entry.box && isBoxAssigned(entry.box),
            left: 0,
        });
    }
    for (const source of fromBoxes) {
        const box = boxes.find(b => b.id === source.boxId);
        source.left = Math.max(0, (box?.items.length ?? 0) - source.units);
    }
    return { total, fresh, takenIndexes: taken.map(e => e.index), fromBoxes };
};
